import {Counter, DropdownMenu, Hotkey, IconButton} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useMemo, useState} from 'react';

import {trackCrossProjectPinnedRuns} from '../../../services/analytics/crossProjectRunsEvents';
import {useViewAction} from '../../../state/views/hooks';
import * as RunSetActions from '../../../state/views/runSet/actions';
import {useStatsigGateCrossProjectPinnedRuns} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {ToggleMinimizeRunSelectorButton} from '../../ToggleMinimizeRunSelectorButton';
import {CrossProjectRunsDrawer} from '../CrossProjectRunsDrawer/CrossProjectRunsDrawer';
import {usePinnedRuns} from '../RunSelectorRunsContext';
import {
  useCurrentRunSetRef,
  useRunSelectorProjectInfo,
  useRunSetGroupSelectionGrouping,
} from '../RunSelectorRunSetContext';
import {type DisabledFeaturesMap} from '../types';
import {isCrossProjectRun} from '../util';

type WBTableTitleActionsProps = {
  disabledFeatures?: DisabledFeaturesMap;
  isTableExpanded?: boolean;
  toggleMinimize?: (method: 'button' | 'hotkey') => void;
  toggleExpanded: () => void;
};

type ActionsChildProps = {
  isMinimizeAllowed?: boolean;
  isTableExpanded?: boolean;
  toggleMinimize?: (method: 'button' | 'hotkey') => void;
  toggleExpanded: () => void;
};

export const WBTableTitleActions = ({
  disabledFeatures,
  isTableExpanded,
  toggleMinimize,
  toggleExpanded,
}: WBTableTitleActionsProps) => {
  const isMinimizeAllowed = !isTableExpanded && toggleMinimize != null;
  const isCrossProjectPinsAllowed =
    useStatsigGateCrossProjectPinnedRuns() &&
    !disabledFeatures?.['pin-cross-project-runs'];
  const childProps: ActionsChildProps = {
    isMinimizeAllowed,
    isTableExpanded,
    toggleMinimize,
    toggleExpanded,
  };

  if (isCrossProjectPinsAllowed) {
    return <WBTableTitleMenu {...childProps} />;
  }

  return <WBTableTitleButtons {...childProps} />;
};

const WBTableTitleButtons = ({
  isMinimizeAllowed,
  isTableExpanded,
  toggleMinimize,
  toggleExpanded,
}: ActionsChildProps) => {
  return (
    <span>
      {isMinimizeAllowed && (
        <ToggleMinimizeRunSelectorButton
          variant="ghost"
          onToggle={() => toggleMinimize?.('button')}
          toMinimize
        />
      )}
      <IconButton
        data-test="expand-toggle"
        aria-label={
          isTableExpanded
            ? 'Collapse runs to sidebar'
            : 'Expand full runs table'
        }
        variant="ghost"
        icon={isTableExpanded ? 'expand-left' : 'expand-right'}
        onPress={toggleExpanded}
        tooltip={{
          children: (
            <>
              {isTableExpanded ? 'Collapse to sidebar' : 'Expand full table'}
              <Hotkey
                ml={4}
                name={
                  isTableExpanded ? 'Collapse to sidebar' : 'Expand full table'
                }
                keys={['Primary', 'KeyJ']}
              />
            </>
          ),
        }}
      />
    </span>
  );
};

const WBTableTitleMenu = ({
  isMinimizeAllowed,
  isTableExpanded,
  toggleMinimize,
  toggleExpanded,
}: ActionsChildProps) => {
  const [isCrossProjectRunsDrawerOpen, setIsCrossProjectRunsDrawerOpen] =
    useState(false);

  const {entityName, projectName} = useRunSelectorProjectInfo();
  const pinnedRuns = usePinnedRuns();
  const runSetRef = useCurrentRunSetRef();
  const bulkUpdatePins = useViewAction(
    runSetRef,
    RunSetActions.setBulkPinnedRuns
  );
  const grouping = useRunSetGroupSelectionGrouping();
  const isGrouped = grouping.length > 0;

  const {currentProjectPins, crossProjectPins} = useMemo(() => {
    const currentProject = {entityName, name: projectName};
    return (pinnedRuns ?? []).reduce(
      (acc, run) => {
        if (isCrossProjectRun(run.project, currentProject)) {
          acc.crossProjectPins.push(run.id);
        } else {
          acc.currentProjectPins.push(run.id);
        }
        return acc;
      },
      {currentProjectPins: [] as string[], crossProjectPins: [] as string[]}
    );
  }, [pinnedRuns, entityName, projectName]);

  const hasCrossProjectPins = crossProjectPins.length > 0;

  const onClearCrossProjectRuns = useCallback(() => {
    trackCrossProjectPinnedRuns({
      action: 'Clear all cross-project runs',
      numRunsRemoved: crossProjectPins.length,
    });
    bulkUpdatePins(currentProjectPins);
  }, [bulkUpdatePins, currentProjectPins, crossProjectPins.length]);

  return (
    <>
      <DropdownMenu.Trigger>
        <IconButton
          icon="overflow-horizontal"
          aria-label="More actions..."
          variant="ghost"
        />
        <DropdownMenu.Content placement="bottom end">
          {isMinimizeAllowed && (
            <DropdownMenu.Item onAction={() => toggleMinimize?.('button')}>
              <DropdownMenu.ItemDecorationLeft
                iconProps={{icon: 'expand-left'}}
              />
              <DropdownMenu.ItemLabel>Hide sidebar</DropdownMenu.ItemLabel>
              <DropdownMenu.ItemDecorationRight
                hotkeyProps={{
                  name: 'Toggle minimize run selector',
                  keys: ['Primary', 'Period'],
                }}
              />
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item onAction={toggleExpanded}>
            <DropdownMenu.ItemDecorationLeft
              iconProps={{
                icon: isTableExpanded ? 'expand-left' : 'expand-right',
              }}
            />
            <DropdownMenu.ItemLabel>
              {isTableExpanded ? 'Collapse to sidebar' : 'Expand full table'}
            </DropdownMenu.ItemLabel>
            <DropdownMenu.ItemDecorationRight
              hotkeyProps={{
                name: isTableExpanded
                  ? 'Collapse to sidebar'
                  : 'Expand full table',
                keys: ['Primary', 'KeyJ'],
              }}
            />
          </DropdownMenu.Item>
          {!isGrouped && (
            <>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                onAction={() => setIsCrossProjectRunsDrawerOpen(true)}>
                <DropdownMenu.ItemDecorationLeft
                  iconProps={{icon: 'folder-eye'}}
                />
                <DropdownMenu.ItemLabel>
                  Select runs from another project
                </DropdownMenu.ItemLabel>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                isDisabled={!hasCrossProjectPins}
                onAction={onClearCrossProjectRuns}>
                <DropdownMenu.ItemDecorationLeft
                  iconProps={{icon: 'clear-all'}}
                />
                <DropdownMenu.ItemLabel>
                  <TailwindContents>
                    <div className="flex items-center gap-6">
                      Clear runs from other projects
                      {hasCrossProjectPins && (
                        <Counter
                          value={crossProjectPins.length}
                          aria-label="runs"
                        />
                      )}
                    </div>
                  </TailwindContents>
                </DropdownMenu.ItemLabel>
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Trigger>
      {isCrossProjectRunsDrawerOpen && (
        <CrossProjectRunsDrawer
          isOpen={isCrossProjectRunsDrawerOpen}
          onOpenChange={setIsCrossProjectRunsDrawerOpen}
        />
      )}
    </>
  );
};
